// src/shared/context/websocket-context.js
import React, {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { AuthContext } from "./auth-context";

const WebSocketContext = createContext();

const RECONNECT_INTERVAL = 1000;
const MAX_RECONNECT_INTERVAL = 30000; // teto do backoff (antes desistia às 5 tentativas)
const HEARTBEAT_INTERVAL = 25000; // de quanto em quanto envia ping
const HEARTBEAT_TIMEOUT = 10000; // quanto espera por atividade antes de assumir morto

export function WebSocketProvider({ children }) {
  const auth = useContext(AuthContext);
  const subscribersRef = useRef(new Map());

  const handleConfiguracaoUpdate = (parsedData) => {
    if (parsedData.tabela !== "configuracaos") return;

    const storedData = JSON.parse(localStorage.getItem("userData"));
    if (!storedData) return;

    const configuracaoAtualizada = parsedData.documento?.configuracoes;
    if (!configuracaoAtualizada) return;

    // Atualizar clinica.configuracoes no localStorage
    const clinicaAtualizada = {
      ...storedData.clinica,
      configuracoes: {
        ...storedData.clinica.configuracoes,
        configuracoes: configuracaoAtualizada,
      },
    };

    const novoStoredData = {
      ...storedData,
      clinica: clinicaAtualizada,
    };

    localStorage.setItem("userData", JSON.stringify(novoStoredData));

    // Atualizar o AuthContext para que os componentes reajam
    auth.login(
      storedData.userId,
      storedData.token,
      storedData.image,
      storedData.role,
      storedData.listaRoles,
      clinicaAtualizada,
      storedData.usuariosAssociados,
      storedData.secretariasDoMedico,
      storedData.assistentesDoMedico,
      storedData.perm,
      storedData.nome,
      storedData.genero,
      storedData.playNotificationSound,
      new Date(storedData.expiration),
      storedData.estabelecimento,
    );
  };

  const socketRef = useRef(null);
  const reconnectAttemptsRef = useRef(0);
  const reconnectTimeoutRef = useRef(null);
  const heartbeatIntervalRef = useRef(null);
  const heartbeatTimeoutRef = useRef(null);
  const [isConnecting, setIsConnecting] = useState(false);
  let subscriberIdCounter = 0;

  // ---------- Heartbeat (deteção de ligações zombie) ----------
  const stopHeartbeat = () => {
    if (heartbeatIntervalRef.current)
      clearInterval(heartbeatIntervalRef.current);
    if (heartbeatTimeoutRef.current)
      clearTimeout(heartbeatTimeoutRef.current);
    heartbeatIntervalRef.current = null;
    heartbeatTimeoutRef.current = null;
  };

  const startHeartbeat = () => {
    stopHeartbeat();
    heartbeatIntervalRef.current = setInterval(() => {
      const socket = socketRef.current;
      if (!socket || socket.readyState !== WebSocket.OPEN) return;

      try {
        socket.send(JSON.stringify({ type: "ping" }));
      } catch {
        // nem conseguimos enviar → ligação morta
        try {
          socket.close();
        } catch {}
        return;
      }

      // se não chegar nenhuma mensagem (pong incluído) a tempo, é zombie
      heartbeatTimeoutRef.current = setTimeout(() => {
        console.warn("⚠️ Heartbeat sem resposta — a forçar reconexão");
        try {
          socket.close(); // dispara onclose → reconecta
        } catch {}
      }, HEARTBEAT_TIMEOUT);
    }, HEARTBEAT_INTERVAL);
  };

  // ---------- Reconexão (sem teto, com backoff limitado) ----------
  const scheduleReconnect = () => {
    if (!auth.token || subscribersRef.current.size === 0) return;

    const timeout = Math.min(
      RECONNECT_INTERVAL * Math.pow(2, reconnectAttemptsRef.current),
      MAX_RECONNECT_INTERVAL,
    );

    if (reconnectTimeoutRef.current) clearTimeout(reconnectTimeoutRef.current);
    reconnectTimeoutRef.current = setTimeout(() => {
      reconnectAttemptsRef.current++;
      connectWebSocket();
    }, timeout);
  };

  const connectWebSocket = () => {
    if (
      socketRef.current?.readyState === WebSocket.CONNECTING ||
      socketRef.current?.readyState === WebSocket.OPEN
    ) {
      return;
    }

    if (!auth.token || isConnecting) return;

    setIsConnecting(true);
    const wsUrl = `${process.env.REACT_APP_WEBSOCKET_ADDRESS}?token=${auth.token}`;

    socketRef.current = new WebSocket(wsUrl);

    socketRef.current.onopen = () => {
      setIsConnecting(false);
      reconnectAttemptsRef.current = 0; // sucesso → reset do backoff
      startHeartbeat();
    };

    socketRef.current.onmessage = (event) => {
      // QUALQUER mensagem prova que a ligação está viva → cancela watchdog
      if (heartbeatTimeoutRef.current) {
        clearTimeout(heartbeatTimeoutRef.current);
        heartbeatTimeoutRef.current = null;
      }

      try {
        const parsedData = JSON.parse(event.data);

        // Pong é só keep-alive, não propagar aos subscritores
        if (parsedData.type === "pong") return;

        // Tratar atualizações de configuração globalmente
        handleConfiguracaoUpdate(parsedData);

        // Notify all subscribers
        subscribersRef.current.forEach((callback, id) => {
          try {
            callback(parsedData);
          } catch (error) {
            console.error(`❌ Erro no Subscriber-${id}:`, error);
          }
        });
      } catch (error) {
        console.error("❌ Erro ao processar mensagem:", error);
      }
    };

    socketRef.current.onclose = () => {
      socketRef.current = null;
      setIsConnecting(false);
      stopHeartbeat();
      scheduleReconnect(); // nunca desiste enquanto houver token + subscritores
    };

    socketRef.current.onerror = (error) => {
      console.error("🔴 Erro no WebSocket:", error);
    };
  };

  useEffect(() => {
    connectWebSocket();

    // Reconectar imediatamente quando a rede volta ou o utilizador volta à aba
    const handleWake = () => {
      if (
        auth.token &&
        (!socketRef.current || socketRef.current.readyState !== WebSocket.OPEN)
      ) {
        reconnectAttemptsRef.current = 0; // reconectar já, sem esperar backoff
        if (reconnectTimeoutRef.current)
          clearTimeout(reconnectTimeoutRef.current);
        connectWebSocket();
      }
    };

    const handleVisibility = () => {
      if (document.visibilityState === "visible") handleWake();
    };

    window.addEventListener("online", handleWake);
    window.addEventListener("focus", handleWake);
    document.addEventListener("visibilitychange", handleVisibility);

    return () => {
      window.removeEventListener("online", handleWake);
      window.removeEventListener("focus", handleWake);
      document.removeEventListener("visibilitychange", handleVisibility);
      stopHeartbeat();
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
      if (socketRef.current) {
        socketRef.current.close();
        socketRef.current = null;
      }
    };
  }, [auth.token]); // eslint-disable-line react-hooks/exhaustive-deps

  const subscribe = (callback, subscriberId) => {
    const id = subscriberId || `sub_${subscriberIdCounter++}`;
    subscribersRef.current.set(id, callback);

    if (!socketRef.current || socketRef.current.readyState !== WebSocket.OPEN) {
      connectWebSocket();
    }

    return () => {
      subscribersRef.current.delete(id);
    };
  };

  return (
    <WebSocketContext.Provider value={{ subscribe }}>
      {children}
    </WebSocketContext.Provider>
  );
}

// Custom hook for components to use
export function useWebSocketSubscription(
  callback,
  deps = [],
  componentName = "Unknown",
) {
  const { subscribe } = useContext(WebSocketContext);

  useEffect(() => {
    const unsubscribe = subscribe(callback, componentName);
    return () => unsubscribe();
  }, deps); // eslint-disable-line react-hooks/exhaustive-deps
}
